import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import type {ColorStorage} from '@Components/color-picker-v2';
import {getCurrentPageBackground, getNumberOfPages} from '@Components/poster-editor/poster-editor-reducer';
import {PosterEditingBaseColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-color-panel';
import type {ColorFillPropertyType} from '@Components/color-fill';
import {getColorFillTypeFromFillType, getColorObjectArrayFromFillColor, getFillTypeFromColorFillType, getRGBAArrayFromColorStorage} from '@Utils/color.util';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {useAppSelector} from '@/hooks';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';
import isEqual from 'lodash/isEqual';

export function PageBackgroundColorPanel(): ReactElement<any> | null {
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);
  const numberOfPages = useAppSelector(getNumberOfPages);
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const initialColorRef = useRef<ColorStorage[] | null>(null);
  const initialTypeRef = useRef<ColorFillPropertyType | undefined>(undefined);

  if (!currentBackgroundObject || !currentPage) {
    throw new Error('current background object or current page object is null!');
  }

  if (currentBackgroundObject.details.type !== BackgroundTypeName.COLOR) {
    return null;
  }

  const colorDetails = currentBackgroundObject.details;

  if (colorDetails.fill.fillType === FillTypes.NONE) {
    return null;
  }

  if (initialColorRef.current === null) {
    initialColorRef.current = getColorObjectArrayFromFillColor(colorDetails.fill.fillColor);
    initialTypeRef.current = getColorFillTypeFromFillType(colorDetails.fill.fillType);
  }

  const getBackgroundColor = (): ColorStorage[] => {
    return getColorObjectArrayFromFillColor(colorDetails.fill.fillColor);
  };

  const onChangeColor = (updatedColor: ColorStorage[]): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          fill: {
            fillColor: getRGBAArrayFromColorStorage(updatedColor),
          },
        },
      },
      {undoable: false}
    );
  };

  const onChangeEnded = (updatedColor: ColorStorage[]): void => {
    void currentPage?.background.updateFromObject({
      details: {
        fill: {
          fillColor: getRGBAArrayFromColorStorage(updatedColor),
        },
      },
    });
  };

  const onChangeType = (newType: ColorFillPropertyType): void => {
    if (!('fill' in currentPage.background.details)) {
      throw new Error('Page background does not have fill property');
    }

    void currentPage.background.updateFromObject({
      details: {
        fill: {
          fillType: getFillTypeFromColorFillType(newType),
          fillColor: currentPage.background.details.fill.getColorForNewType(getFillTypeFromColorFillType(newType)),
        },
      },
    });
  };

  const onApplyToAllPages = async (colorToApply: ColorStorage[]): Promise<void> => {
    const poster = window.posterEditor?.whiteboard;
    const pagesHashMap = poster?.pages.pagesHashMap;
    if (!pagesHashMap || !poster) {
      return;
    }

    const updates = Object.values(pagesHashMap)
      .filter((page) => page.background.details.type !== BackgroundTypeName.IMAGE)
      .map((page) =>
        page.background.updateFromObject(
          {
            details: {
              fill: {
                fillColor: getRGBAArrayFromColorStorage(colorToApply),
              },
            },
          },
          {undoable: false}
        )
      );

    await Promise.all(updates);
    poster.history.addPosterHistory();
  };

  return (
    <PosterEditingBaseColorPanel
      id="page-background-color-panel"
      type={getColorFillTypeFromFillType(colorDetails.fill.fillType)}
      isGradientEnabled
      color={getBackgroundColor()}
      onChangeColor={onChangeColor}
      onChangeType={onChangeType}
      onChangeEnded={onChangeEnded}
      isAlphaDisabled
      footerOption={
        numberOfPages > 1 && !isEqual(initialColorRef.current, getBackgroundColor())
          ? {
              show: true,
              initialColor: initialColorRef.current ?? [],
              initialType: initialTypeRef.current ?? undefined,
              buttonText: window.i18next.t('pmwjs_apply_to_all_pages'),
              onButtonClick: onApplyToAllPages,
            }
          : undefined
      }
    />
  );
}
